class V2TestimonialSlider extends HTMLElement {
  constructor() {
    super()
    this.slides = this.querySelector("v2-testimonial-slides")
    this.prevBtn = this.querySelector('button[name="previous"]')
    this.nextBtn = this.querySelector('button[name="next"]')
    this.slideItems = this.querySelectorAll(".v2-testimonial-slide")
    this.bulletContainer = this.querySelector("v2-testimonial-nav")

    this.bullets = []
    this.activePageIndex = 0
    this.cardsPerPage = 1
    this.totalPages = 1
    this.scrollTicking = false
    this.isScrolling = false
    this.scrollDebounceTimer = null

    if (!this.slides || this.slideItems.length === 0) return

    // Below-the-fold: defer init until component is near viewport
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          io.disconnect()
          this.init()
        }
      },
      { rootMargin: "200px" }
    )
    io.observe(this)
  }

  init() {
    // Calculate initial page layout
    this.cardsPerPage = this.getCardsPerPage()
    this.totalPages = Math.ceil(this.slideItems.length / this.cardsPerPage)
    this.generateBullets()

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", this.onButtonClick.bind(this))
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", this.onButtonClick.bind(this))
    }

    // Throttled scroll handler for button states
    this.slides.addEventListener("scroll", () => {
      if (!this.scrollTicking) {
        requestAnimationFrame(() => {
          this.updateButtonStates()
          this.scrollTicking = false
        })
        this.scrollTicking = true
      }

      // Debounced fallback for browsers without scrollend
      clearTimeout(this.scrollDebounceTimer)
      this.scrollDebounceTimer = setTimeout(() => {
        this.onScrollSettled()
      }, 100)
    })

    // scrollend fires once after scroll animation completes
    if ("onscrollend" in window) {
      this.slides.addEventListener("scrollend", () => {
        clearTimeout(this.scrollDebounceTimer)
        this.onScrollSettled()
      })
    }

    // ResizeObserver: recalculate pages when container width changes
    this.resizeObserver = new ResizeObserver(() => {
      const newCardsPerPage = this.getCardsPerPage()
      if (newCardsPerPage !== this.cardsPerPage) {
        this.cardsPerPage = newCardsPerPage
        const newTotalPages = Math.ceil(
          this.slideItems.length / this.cardsPerPage
        )

        if (newTotalPages !== this.totalPages) {
          this.totalPages = newTotalPages
          // Clamp active page to valid range
          this.activePageIndex = Math.min(
            this.activePageIndex,
            this.totalPages - 1
          )
          this.generateBullets()
          this.updateActiveBullet(this.activePageIndex)
        }

        // Snap to nearest valid page position
        this.scrollToPage(this.activePageIndex)
      }
    })
    this.resizeObserver.observe(this.slides)

    this.updateButtonStates()
  }

  getCardsPerPage() {
    if (!this.slideItems[0]) return 1
    const containerWidth = this.slides.offsetWidth
    const cardWidth = this.slideItems[0].offsetWidth
    return Math.max(1, Math.round(containerWidth / cardWidth))
  }

  generateBullets() {
    // Clear existing bullets
    this.bulletContainer.innerHTML = ""
    this.bullets = []

    for (let i = 0; i < this.totalPages; i++) {
      const bullet = document.createElement("button")
      bullet.className = "v2-testimonial-bullet"
      bullet.setAttribute(
        "aria-selected",
        i === this.activePageIndex ? "true" : "false"
      )
      bullet.setAttribute("aria-label", `Go to page ${i + 1}`)
      bullet.dataset.index = i

      bullet.addEventListener("click", (e) => {
        e.preventDefault()
        if (this.isScrolling) return
        this.isScrolling = true
        this.activePageIndex = i
        this.updateActiveBullet(i)
        this.scrollToPage(i)
      })

      this.bulletContainer.appendChild(bullet)
      this.bullets.push(bullet)
    }
  }

  scrollToPage(pageIndex) {
    const targetCardIndex = pageIndex * this.cardsPerPage
    // Clamp to last card if we'd overshoot
    const clampedIndex = Math.min(targetCardIndex, this.slideItems.length - 1)
    const target = this.slideItems[clampedIndex]
    if (target) {
      // Subtract the container's padding-left so page 0 scrolls to 0
      // and subsequent pages align with the padded snap boundary.
      // Falls back to plain offsetLeft when there is no padding.
      const paddingLeft =
        parseFloat(getComputedStyle(this.slides).paddingLeft) || 0
      this.slides.scrollTo({
        left: target.offsetLeft - paddingLeft,
        behavior: this.getScrollBehavior(),
      })
    }
  }

  onScrollSettled() {
    this.isScrolling = false
    this.updateActiveFromScroll()
  }

  updateActiveFromScroll() {
    const scrollLeft = this.slides.scrollLeft
    const maxScrollLeft = this.slides.scrollWidth - this.slides.clientWidth

    // If at the very end, snap to last page
    if (Math.ceil(scrollLeft) >= maxScrollLeft - 2) {
      this.activePageIndex = this.totalPages - 1
    } else {
      // Calculate page from scroll position
      const pageWidth = this.slides.clientWidth
      this.activePageIndex = Math.min(
        Math.round(scrollLeft / pageWidth),
        this.totalPages - 1
      )
    }

    this.updateActiveBullet(this.activePageIndex)
    this.updateButtonStates()
  }

  getScrollBehavior() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth"
  }

  updateActiveBullet(pageIndex) {
    // Fixed CSS values - no reliance on offsetWidth during transitions
    const INACTIVE_W = 6
    const ACTIVE_W = 36
    const GAP = 12
    const PAD = 16

    this.bullets.forEach((bullet) => {
      const bulletIndex = parseInt(bullet.dataset.index, 10)
      if (bulletIndex === pageIndex) {
        bullet.setAttribute("aria-selected", "true")
      } else {
        bullet.setAttribute("aria-selected", "false")
      }
    })

    if (this.bulletContainer) {
      // Calculate where the active bullet's center WILL BE after transitions
      // All bullets before it are inactive width, the active one is active width
      let targetCenter = PAD
      for (let i = 0; i < pageIndex; i++) {
        targetCenter += INACTIVE_W + GAP
      }
      targetCenter += ACTIVE_W / 2

      const containerWidth = this.bulletContainer.offsetWidth
      const scrollPos = targetCenter - containerWidth / 2

      this.bulletContainer.scrollTo({
        left: scrollPos,
        behavior: this.getScrollBehavior(),
      })
    }
  }

  onButtonClick(event) {
    event.preventDefault()

    // Guard: ignore clicks while a scroll animation is in progress
    if (this.isScrolling) return

    const isNext = event.currentTarget.name === "next"

    if (isNext && this.activePageIndex < this.totalPages - 1) {
      this.activePageIndex++
    } else if (!isNext && this.activePageIndex > 0) {
      this.activePageIndex--
    } else {
      // Edge case: pagination shows one page but container still has scroll room.
      // Scroll to the absolute edge so the content doesn't feel stuck.
      const scrollLeft = this.slides.scrollLeft
      const maxScrollLeft = this.slides.scrollWidth - this.slides.clientWidth
      if (isNext && Math.ceil(scrollLeft) < maxScrollLeft - 1) {
        this.isScrolling = true
        this.slides.scrollTo({
          left: maxScrollLeft,
          behavior: this.getScrollBehavior(),
        })
      } else if (!isNext && scrollLeft > 1) {
        this.isScrolling = true
        this.slides.scrollTo({
          left: 0,
          behavior: this.getScrollBehavior(),
        })
      }
      return
    }

    this.isScrolling = true
    this.updateActiveBullet(this.activePageIndex)
    this.scrollToPage(this.activePageIndex)
  }

  updateButtonStates() {
    const scrollLeft = this.slides.scrollLeft
    const scrollWidth = this.slides.scrollWidth
    const clientWidth = this.slides.clientWidth

    if (this.prevBtn) {
      this.prevBtn.disabled = scrollLeft <= 1
    }
    if (this.nextBtn) {
      this.nextBtn.disabled =
        Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 2
    }
  }
}

customElements.define("v2-testimonial-slider", V2TestimonialSlider)
